/**
 * WorkflowHelp
 *
 * Right panel providing context-sensitive help, tips, and issue display.
 * Shows "Why we ask" explanations, common mistakes, and relevant blockers/warnings.
 * Collapsible with localStorage persistence.
 *
 * Part of Phase 8: Job Workflow Wizard
 */

import { useState, useEffect } from 'react';
import { type WorkflowStepId, type WorkflowIssue } from '@/types/workflow';
import { getStepHelp, getSubstepHelp } from '@/lib/workflow-steps';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  HelpCircle,
  Lightbulb,
  AlertTriangle,
  XCircle,
  Info,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { JobData } from './WorkflowContainer';

const STORAGE_KEY = 'workflow-help-collapsed';
const STORAGE_KEY_USER_TOGGLED = 'workflow-help-user-toggled';

interface WorkflowHelpProps {
  currentStepId: WorkflowStepId;
  currentSubstepId?: string;
  jobData: JobData;
  issues: {
    blockers: WorkflowIssue[];
    warnings: WorkflowIssue[];
    info: WorkflowIssue[];
  };
  isMobile?: boolean;
}

export function WorkflowHelp({
  currentStepId,
  currentSubstepId,
  jobData,
  issues,
  isMobile = false,
}: WorkflowHelpProps) {
  const stepHelp = getStepHelp(currentStepId);
  const substepHelp = currentSubstepId
    ? getSubstepHelp(currentStepId, currentSubstepId)
    : undefined;

  // Track if user has manually toggled (for progressive disclosure)
  const [userHasToggled, setUserHasToggled] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY_USER_TOGGLED) === 'true';
    }
    return false;
  });

  // Progressive disclosure:
  // - Default to COLLAPSED (less overwhelming)
  // - Auto-expand if blockers exist AND user hasn't manually collapsed
  // - Respect user's explicit choice after they've toggled
  const hasBlockers = issues.blockers.length > 0;

  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved !== null) {
        return saved === 'true';
      }
    }
    // Default to collapsed unless there are blockers
    return !hasBlockers;
  });

  // Auto-expand when blockers appear (only if user hasn't explicitly collapsed)
  useEffect(() => {
    if (hasBlockers && !userHasToggled) {
      setIsCollapsed(false);
    }
  }, [hasBlockers, userHasToggled]);

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(isCollapsed));
  }, [isCollapsed]);

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
    setUserHasToggled(true);
    localStorage.setItem(STORAGE_KEY_USER_TOGGLED, 'true');
  };

  const hasIssues =
    issues.blockers.length > 0 ||
    issues.warnings.length > 0 ||
    issues.info.length > 0;

  // Mobile version - just render content without aside wrapper
  if (isMobile) {
    return (
      <div className="space-y-4">
        {/* Issues Section (show first if any) */}
        {hasIssues && (
          <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Issues for this step
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <Accordion type="multiple" defaultValue={[]} className="w-full">
                {/* Blockers */}
                {issues.blockers.length > 0 && (
                  <AccordionItem value="blockers" className="border-0">
                    <AccordionTrigger className="py-2 text-sm hover:no-underline">
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-red-500" />
                        <span className="font-medium">
                          Blockers ({issues.blockers.length})
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 pl-6">
                        {issues.blockers.map((issue) => (
                          <li
                            key={issue.id}
                            className="text-sm text-red-700 dark:text-red-400"
                          >
                            <span className="font-medium">{issue.message}</span>
                            {issue.details && (
                              <p className="text-xs text-red-600/70 dark:text-red-400/70 mt-0.5">
                                {issue.details}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                )}
                {/* Warnings */}
                {issues.warnings.length > 0 && (
                  <AccordionItem value="warnings" className="border-0">
                    <AccordionTrigger className="py-2 text-sm hover:no-underline">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                        <span className="font-medium">
                          Warnings ({issues.warnings.length})
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 pl-6">
                        {issues.warnings.map((issue) => (
                          <li
                            key={issue.id}
                            className="text-sm text-amber-700 dark:text-amber-400"
                          >
                            {issue.message}
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                )}
                {/* Info */}
                {issues.info.length > 0 && (
                  <AccordionItem value="info" className="border-0">
                    <AccordionTrigger className="py-2 text-sm hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Info className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">
                          Info ({issues.info.length})
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 pl-6">
                        {issues.info.map((issue) => (
                          <li
                            key={issue.id}
                            className="text-sm text-muted-foreground"
                          >
                            {issue.message}
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </CardContent>
          </Card>
        )}

        {/* Context Card - Why we ask */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">{stepHelp.title}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{stepHelp.description}</p>
            {substepHelp && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  {substepHelp.title}
                </p>
                <p className="text-sm text-muted-foreground">
                  {substepHelp.description}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tips */}
        {stepHelp.tips && stepHelp.tips.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-base">Tips</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {stepHelp.tips.map((tip, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex gap-2">
                    <span className="text-muted-foreground flex-shrink-0">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Jurisdiction Info */}
        {jobData.jurisdictionAhjName && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-base">Jurisdiction</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="font-medium">{jobData.jurisdictionAhjName}</p>
              {jobData.jurisdictionCode && (
                <p className="text-sm text-muted-foreground">
                  Code: {jobData.jurisdictionCode}
                </p>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {jobData.jurisdictionOverlays?.isHvhz && (
                  <Badge
                    variant="secondary"
                    className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                  >
                    <Zap className="h-3 w-3 mr-1" />
                    HVHZ
                  </Badge>
                )}
                {jobData.jurisdictionOverlays?.isFloodZone && (
                  <Badge variant="secondary" className="text-xs">
                    Flood Zone
                  </Badge>
                )}
                {jobData.jurisdictionOverlays?.isCoastalZone && (
                  <Badge variant="secondary" className="text-xs">
                    Coastal
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Desktop version with collapsible aside
  return (
    <aside
      className={cn(
        'hidden lg:flex flex-col border-l bg-muted/30 transition-all duration-200',
        isCollapsed ? 'w-12' : 'w-80'
      )}
    >
      {/* Toggle Button */}
      <div
        className={cn(
          'flex items-center border-b p-2',
          isCollapsed ? 'justify-center' : 'justify-between'
        )}
      >
        {!isCollapsed && (
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-2">
            Help & Context
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleCollapsed}
          className="h-8 w-8 p-0"
          title={isCollapsed ? 'Show help panel' : 'Hide help panel'}
        >
          {isCollapsed ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Collapsed State - Just show icons */}
      {isCollapsed ? (
        <div className="flex-1 flex flex-col items-center pt-4 gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleCollapsed}
            className="h-8 w-8 p-0"
            title="Show help"
          >
            <HelpCircle className="h-5 w-5 text-primary" />
          </Button>
          {hasIssues && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleCollapsed}
              className="h-8 w-8 p-0 relative"
              title="Show issues"
            >
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {issues.blockers.length > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-micro text-white flex items-center justify-center">
                  {issues.blockers.length}
                </span>
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleCollapsed}
            className="h-8 w-8 p-0"
            title="Show tips"
          >
            <Lightbulb className="h-5 w-5 text-muted-foreground" />
          </Button>
          {jobData.jurisdictionAhjName && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleCollapsed}
              className="h-8 w-8 p-0"
              title="Show jurisdiction info"
            >
              <MapPin className="h-5 w-5 text-muted-foreground" />
            </Button>
          )}
        </div>
      ) : (
        /* Expanded State - Full content */
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {/* Issues Section (show first if any) */}
            {hasIssues && (
              <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    Issues for this step
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <Accordion type="multiple" defaultValue={[]} className="w-full">
                    {/* Blockers */}
                    {issues.blockers.length > 0 && (
                      <AccordionItem value="blockers" className="border-0">
                        <AccordionTrigger className="py-2 text-sm hover:no-underline">
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-500" />
                            <span className="font-medium">
                              Blockers ({issues.blockers.length})
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul className="space-y-2 pl-6">
                            {issues.blockers.map((issue) => (
                              <li
                                key={issue.id}
                                className="text-sm text-red-700 dark:text-red-400"
                              >
                                <span className="font-medium">
                                  {issue.message}
                                </span>
                                {issue.details && (
                                  <p className="text-xs text-red-600/70 dark:text-red-400/70 mt-0.5">
                                    {issue.details}
                                  </p>
                                )}
                                {issue.fixAction && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="h-auto p-0 text-xs text-red-600 dark:text-red-400"
                                  >
                                    {issue.fixAction} →
                                  </Button>
                                )}
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    )}

                    {/* Warnings */}
                    {issues.warnings.length > 0 && (
                      <AccordionItem value="warnings" className="border-0">
                        <AccordionTrigger className="py-2 text-sm hover:no-underline">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            <span className="font-medium">
                              Warnings ({issues.warnings.length})
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul className="space-y-2 pl-6">
                            {issues.warnings.map((issue) => (
                              <li
                                key={issue.id}
                                className="text-sm text-amber-700 dark:text-amber-400"
                              >
                                {issue.message}
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    )}

                    {/* Info */}
                    {issues.info.length > 0 && (
                      <AccordionItem value="info" className="border-0">
                        <AccordionTrigger className="py-2 text-sm hover:no-underline">
                          <div className="flex items-center gap-2">
                            <Info className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">
                              Info ({issues.info.length})
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul className="space-y-2 pl-6">
                            {issues.info.map((issue) => (
                              <li
                                key={issue.id}
                                className="text-sm text-muted-foreground"
                              >
                                {issue.message}
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    )}
                  </Accordion>
                </CardContent>
              </Card>
            )}

            {/* Context Card - Why we ask */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-primary" />
                  <CardTitle className="text-base">{stepHelp.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {stepHelp.description}
                </p>
                {substepHelp && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      {substepHelp.title}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {substepHelp.description}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tips */}
            {stepHelp.tips && stepHelp.tips.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-muted-foreground" />
                    <CardTitle className="text-base">Tips</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {stepHelp.tips.map((tip, index) => (
                      <li
                        key={index}
                        className="text-sm text-muted-foreground flex gap-2"
                      >
                        <span className="text-muted-foreground flex-shrink-0">•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Common Mistakes */}
            {stepHelp.commonMistakes && stepHelp.commonMistakes.length > 0 && (
              <Card className="border-amber-200 dark:border-amber-800">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    <CardTitle className="text-base">Common Mistakes</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {stepHelp.commonMistakes.map((mistake, index) => (
                      <li
                        key={index}
                        className="text-sm text-muted-foreground flex gap-2"
                      >
                        <span className="text-amber-500 flex-shrink-0">!</span>
                        <span>{mistake}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Jurisdiction Info (if available) */}
            {jobData.jurisdictionAhjName && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <CardTitle className="text-base">Jurisdiction</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="font-medium">{jobData.jurisdictionAhjName}</p>
                  {jobData.jurisdictionCode && (
                    <p className="text-sm text-muted-foreground">
                      Code: {jobData.jurisdictionCode}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {jobData.jurisdictionOverlays?.isHvhz && (
                      <Badge
                        variant="secondary"
                        className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                      >
                        <Zap className="h-3 w-3 mr-1" />
                        HVHZ
                      </Badge>
                    )}
                    {jobData.jurisdictionOverlays?.isFloodZone && (
                      <Badge variant="secondary" className="text-xs">
                        Flood Zone
                      </Badge>
                    )}
                    {jobData.jurisdictionOverlays?.isCoastalZone && (
                      <Badge variant="secondary" className="text-xs">
                        Coastal
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Related Topics */}
            {stepHelp.relatedTopics && stepHelp.relatedTopics.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Info className="h-5 w-5 text-muted-foreground" />
                    <CardTitle className="text-base">Related Topics</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1">
                    {stepHelp.relatedTopics.map((topic, index) => (
                      <li key={index}>
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-sm text-primary"
                        >
                          {topic.label} →
                        </Button>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>
      )}
    </aside>
  );
}
